import './docReader.scss';
import React, { useCallback, useEffect, useRef } from 'react';
import { lang } from '@umu/ukit-lang';
import { $window } from '@umu/umu-dom';
import { ResourceStatus, IUserInfo, IWaterMarkProps } from '@umu/umu-types';
import { Loading } from 'common/src/components';
import { getPdfJsWorker } from './util';
import umuWebview from '@umu/umu-webview';
import { isNumber } from './util';
import { configRenderHelper } from './services/configRenderHelper';
import { ToolBar } from './components/toolbar';
import { SCREEN_STATE, IPdfImages } from './base';
import { TranscodingStatus } from './components/transcodingStatus';
import { WaterMarkWrap } from './components/waterMarkWrap';
import { useDocReader, useDocReaderDispatch } from './store/docReaderContext';
import useDocReaderKeyStrokes from './services/useDocReaderKeyStrokes';
import DocPage from './components/docPage';
import Swipe from './components/swiper';
import useDocReaderScale from './services/useDocReaderScale';
import PdfList from './components/pdfList';
import { useLocalStorage } from './hooks/useLocalStorage';
import useUpdateDocReaderOutline from './services/useUpdateDocReaderOutline';
import OutlineSidebar from './components/outlineSidebar';
import { debounce, getPDFDomain } from '@umu/umu-util';

export interface IProps {
    style?: React.CSSProperties;
    className?: string;
    offsetTop?: number; // 顶部偏移量
    documentTitle?: string; // 文档标题
    userInfo?: IUserInfo; // 创建人信息
    resourceUrl: string; // 文档 recourse url
    pdfImages?: IPdfImages; // pdf 图片列表
    initPageNum?: any; // 初始页码
    screenState?: SCREEN_STATE; // 全屏状态
    transStatus?: ResourceStatus; // 转码状态
    waterMarkOptions?: IWaterMarkProps; // 水印配置
    isShowWatermark?: boolean; // 是否开启水印
    containerHeight?: number;
    elementId?: string;
    screenCallback?: (isFullscreen: boolean) => void; // 全屏按钮回调
    pageChangeCallback?: (pageNum: number, allPageNum: number) => void; // 页码切换回调
}

const DocReaderToolBar = ({ offsetTop = 0 }: { offsetTop: number }) => {
    return <ToolBar offsetTop={offsetTop}></ToolBar>;
};

const DocReaderContent = React.memo((props: IProps) => {
    const pdfContentRef = useRef<HTMLDivElement>(null);
    const [state, setState] = useDocReader();
    const dispatch = useDocReaderDispatch();
    const { transStatus } = props;

    const { loading, screenState } = state;
    useEffect(() => {
        const { screenState } = state;
        if (screenState === 'full') {
            umuWebview.api.requestFullscreen();
            dispatch({ type: 'zoomReset' });
        } else {
            umuWebview.api.exitFullscreen();
            dispatch({ type: 'zoomReset' });
        }
    }, [state.screenState]);

    useEffect(() => {
        setState({
            pdfContentRef,
        });
    }, [pdfContentRef]);

    // 绑定键盘事件
    useDocReaderKeyStrokes();
    // 绑定缩放事件
    useDocReaderScale(props.containerHeight);

    if (transStatus === ResourceStatus.WAIT_TRANSCODING) {
        return null;
    }
    if (loading) {
        return <Loading />;
    }
    if (state.networkError) {
        return (
            <div className="doc-reader-network-error">
                <div className="doc-reader-network-error-img" />
                <div className="doc-reader-network-error-tips">{lang('资源加载失败请刷新后再试')}</div>
                <div className="doc-reader-network-error-btn" onClick={() => window.location.reload()}>
                    {lang('刷新')}
                </div>
            </div>
        );
    }
    return (
        <div
            className="pdf-content"
            onClick={() => {
                dispatch({ type: 'toolbarToggle' });
            }}
        >
            <div className="pdf-content-wrap" ref={pdfContentRef}>
                {screenState === 'full' && state.pageFlipMode === 'vertical' ? (
                    <PdfList>
                        {Array.from({ length: state.totalPages }).map((_, index) => {
                            return <DocPage pageNum={index + 1} key={index} />;
                        })}
                    </PdfList>
                ) : (
                    <Swipe>
                        {Array.from({ length: state.totalPages }).map((_, index) => {
                            return <DocPage pageNum={index + 1} key={index} />;
                        })}
                    </Swipe>
                )}
            </div>
        </div>
    );
});

const DocReader = React.memo((props: IProps) => {
    const docContainerRef = useRef<HTMLDivElement>(null);
    const {
        style,
        className,
        resourceUrl,
        transStatus,
        waterMarkOptions,
        userInfo = {
            user_name: '',
        },
        offsetTop = 0,
        isShowWatermark = false,
    } = props;
    const [state, setState] = useDocReader();
    const dispatch = useDocReaderDispatch();
    const totalPagesRef = useRef(state.totalPages);
    totalPagesRef.current = state.totalPages;

    // 初始化pdf
    const initPdf = () => {
        if (typeof $window.pdfjsLib === 'undefined') {
            throw new Error('need pdf SDK, please loaded it');
        }

        const resourceUrl = props.resourceUrl + '?v=mobile';
        const scale = state.scale;

        // 现有的 types 版本与库代码不同步，无法使用，就不增加详细的类型声明了
        // api详见 https://mozilla.github.io/pdf.js/api/
        $window.pdfjsLib.GlobalWorkerOptions.workerSrc = getPdfJsWorker();

        const param = {
            url: resourceUrl,
            cMapUrl: `${getPDFDomain()}/statics/pdfjs-dist@2.0.489/cmaps/`,
            cMapPacked: true,
        };
        const pdfResource = $window.pdfjsLib.getDocument(param);
        pdfResource.promise.then(
            (pdf: any) => {
                setState({ pdf, loading: false, scale, totalPages: pdf.numPages });
            },
            (reason: any) => {
                console.error('initPdf:reason', reason);
                dispatch({ type: 'networkError' });
            },
        );
    };

    useEffect(() => {
        const initPage = Number(props.initPageNum) || 1;
        if (initPage === 1 && state.totalPages === 1) {
            dispatch({ type: 'updateDisplayPageNum', pageNum: 1 });
        }
    }, [props.initPageNum, state.totalPages]);

    // 注册事件，初始化pdf
    useEffect(() => {
        const { resourceUrl, transStatus } = props;
        if (resourceUrl && transStatus !== ResourceStatus.WAIT_TRANSCODING) {
            setTimeout(
                () => {
                    initPdf();
                },
                typeof $window.pdfjsLib === 'undefined' ? 2000 : 0,
            );
        }
    }, []);

    const [flippingMode, setFlippingMode] = useLocalStorage(
        'UMU_DOC_FLIPPING_MODE',
        'horizontal' as typeof state.pageFlipMode,
    );

    // 自动记录翻页模式
    useEffect(() => {
        const { pageFlipMode } = state;
        setFlippingMode(pageFlipMode);
        dispatch({ type: 'pageJump', pageNum: state.displayPageNum });

        const scrollHandler = debounce(() => {
            const { scrollTop, scrollHeight, offsetHeight } = docContainerRef.current!;
            const lastPage = document.getElementById(`${totalPagesRef.current - 1}`);
            if (!!lastPage && scrollTop + offsetHeight + 30 > scrollHeight) {
                dispatch({ type: 'updateDisplayPageNum', pageNum: totalPagesRef.current });
            }
        }, 1000);
        if (state.pageFlipMode === 'vertical') {
            docContainerRef.current?.addEventListener('scroll', scrollHandler);
        }
        return () => {
            docContainerRef.current?.removeEventListener('scroll', scrollHandler);
        };
    }, [state.pageFlipMode]);

    // 初始化页面状态
    useEffect(() => {
        const { userInfo } = props;
        let initPageNum = props.initPageNum || 1;
        if (isNumber(initPageNum) && initPageNum < 1) {
            initPageNum = 1;
        }

        setState({
            pageNum: { value: Number(initPageNum) || 1 }, // 当前页码
            screenState: props.screenState || 'no-full', // 全屏状态  noFull：非全屏 ， full： 全屏
            screenCallback: props.screenCallback,
            pageChangeCallback: props.pageChangeCallback,
            pageFlipMode: flippingMode,
            docContainerRef,
            docReaderProps: {
                ...props,
                initPageNum: props.initPageNum || 1,
                documentTitle: props.documentTitle || '',
            },
            renderType: configRenderHelper.getRenderType(
                props.resourceUrl || '',
                props.elementId || '',
                userInfo ? userInfo.enterprise_id : '',
                props.pdfImages,
            ),
        });
    }, [props]);
    useUpdateDocReaderOutline();

    const onDocumentContextMenu = useCallback((e: React.SyntheticEvent) => {
        e.preventDefault();
        return false;
    }, []);

    if ((!resourceUrl && (!transStatus || transStatus === ResourceStatus.IN_USE)) || state.isError) {
        return (
            <div className="doc-container">
                <p className="empty-tips">{lang('该内容已删除')}</p>
            </div>
        );
    }

    return (
        <div className={`doc-reader ${state.screenState} ${className}`} style={style}>
            <OutlineSidebar></OutlineSidebar>
            <div className={`doc-container ${state.screenState}`} ref={docContainerRef}>
                <div className="doc-container-wrap" onContextMenu={onDocumentContextMenu}>
                    <DocReaderToolBar offsetTop={offsetTop}></DocReaderToolBar>
                    <TranscodingStatus transStatus={props.transStatus}></TranscodingStatus>
                    <DocReaderContent {...props}></DocReaderContent>
                    {/* 只有渲染完毕后，才开始渲染水印 */}
                    {state?.isRendered && isShowWatermark && (
                        <WaterMarkWrap
                            waterMarkOptions={waterMarkOptions}
                            isFullscreen={state.screenState === 'full'}
                            scale={state.scale}
                            defaultText={userInfo.user_name}
                        ></WaterMarkWrap>
                    )}
                </div>
            </div>
        </div>
    );
});

export default DocReader;
